/**
 * Editorial-light theme preset – cinematic, photography-forward, magazine-spread
 * on a warm cream surface. Sibling to `editorial-dark` (GEN-1771).
 *
 * Designed for orthopedic / sports-medicine / surgical / aesthetic / dental
 * providers who want the same magazine register as editorial-dark but with
 * a light chromatic palette. The fidelity bar is identical to editorial-dark
 * ("looks as good as the reference site"); only the surface flips.
 *
 * Reference spec: docs/engineering/2026-05-11-editorial-light-preset.md
 * Hard rules:    docs/engineering/2026-05-11-editorial-light-charter.md
 * Sibling:       docs/engineering/2026-05-07-editorial-dark-charter.md
 *
 * Design pillars (Charter):
 * - Single saturated accent (terracotta #C26B47) on cream/off-white surfaces.
 * - Two-tone H1 typography (typed spans – same as editorial-dark).
 * - Generous vertical rhythm + cinematic motion choreography (unchanged from dark).
 * - Photography earns every slot (`buildPhotoPrompt` with `register: 'light'`).
 *
 * AI-NOTE: This preset MUST conform to `ThemeSchema.parse({...})` at module
 * load time (acceptance test asserts). Shape mirrors editorial-dark exactly –
 * only color tokens, scrim direction, and font path differ.
 *
 * AI-NOTE: Self-hosted Manrope woff2 with preload (NOT Google CDN). Same font
 * files as editorial-dark – they are visually identical and the path is forked
 * only so each preset can ship independent fingerprints and a deploy-gate-
 * isolated set of woff2 files.
 *
 * AI-NOTE: `accentMuted`, `accentDim`, `border` rgba tonal variants are NOT
 * declared as theme tokens (would fail `HexColorSchema`). Encode at use site
 * via `color-mix(in srgb, var(--color-accent) 60%, transparent)`.
 */

import { ThemeSchema, type Theme } from '../types';

export const editorialLightTheme: Theme = ThemeSchema.parse({
  name: 'editorial-light',
  colors: {
    // Primary: warm terracotta accent (darkened for AA contrast; original light
    // shade preserved as primaryLight for decorative use)
    primary: '#9E4E2F',
    primaryDark: '#7C3B22',
    primaryLight: '#C26B47',
    // Accent mirrors primary in this preset (single accent register)
    accent: '#9E4E2F',
    accentDark: '#7C3B22',
    accentLight: '#C26B47',
    // Surfaces – warm cream / off-white (NOT pure white)
    background: '#FAF7F2',
    backgroundAlt: '#F2EEE6',
    surface: '#FFFFFF',
    // Text
    text: '#1A1916',
    textMuted: '#6B6660',
    textInverse: '#FAF7F2',
    // Borders – strict hex; tonal variants encoded via color-mix() at use site
    border: '#E5DFD3',
    // Status (deepened for light-surface legibility – same hues as dark preset's
    // semantic palette, recalibrated for contrast against cream)
    success: '#15803D',
    warning: '#B45309',
    error: '#B91C1C',
    // Medical-specific (mirrors editorial-dark semantic palette)
    evidenceStrong: '#15803D',
    evidenceModerate: '#B45309',
    evidenceLow: '#6B6660',
    safetyWarning: '#B91C1C',
    safetyInfo: '#C26B47',
  },
  // P3 alternates omitted in V1 – sRGB only. Add when first provider needs them.
  typography: {
    // Manrope used for both display and body. Weight contrast (800 vs 400)
    // creates the editorial register without a second family. Same as dark.
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontFamilyHeading: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontWeightHeading: '800',
    fontSizeBase: '1.0625rem', // 17px – slightly larger body for editorial register
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.25rem',
    fontSizeXl: '1.5rem',
    fontSize2xl: '2rem',
    fontSize3xl: 'clamp(2.5rem, 5vw, 3.25rem)',
    fontSize4xl: 'clamp(3rem, 6vw, 4.5rem)',
    lineHeightBase: '1.6',
    lineHeightTight: '1.05',
    lineHeightLoose: '1.8',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '8px',
    md: '14px',
    lg: '20px',
    xl: '28px',
    full: '9999px',
  },
  // Light-surface-tuned shadows; lower alpha than dark preset because shadows
  // on light backgrounds read as soft elevation, not high contrast.
  shadows: {
    sm: '0 1px 2px 0 rgb(26 25 22 / 0.04)',
    md: '0 4px 8px -2px rgb(26 25 22 / 0.06), 0 2px 4px -1px rgb(26 25 22 / 0.04)',
    lg: '0 12px 24px -6px rgb(26 25 22 / 0.10), 0 4px 8px -2px rgb(26 25 22 / 0.06)',
    xl: '0 24px 48px -12px rgb(26 25 22 / 0.14), 0 8px 16px -4px rgb(26 25 22 / 0.08)',
  },
  layout: {
    heroStyle: 'image',
    cardStyle: 'minimal',
    iconStyle: 'geometric',
    buttonStyle: 'rounded',
    sectionPadding: 'clamp(4rem, 10vw, 9rem)',
    contentMaxWidth: '72rem',
  },
  // Motion tokens – identical to editorial-dark.
  motion: {
    durationInstant: '0ms',
    durationFast: '150ms',
    durationNormal: '200ms',
    durationModerate: '250ms',
    durationEditorial: '600ms',
    easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
    easeIn: 'cubic-bezier(0.55, 0, 1, 0.45)',
    easeInOut: 'cubic-bezier(0.45, 0, 0.55, 1)',
    easeEditorial: 'cubic-bezier(0.22, 1, 0.36, 1)',
    distanceShort: '8px',
    distanceMedium: '16px',
    distanceLong: '32px',
    parallaxMax: '8%',
  },
  // Density tokens – identical to editorial-dark. Magazine rhythm is preset-
  // agnostic: 7-11 sections on the homepage, same gap scale, same gutters.
  density: {
    homeSectionGap: '4rem',
    homeSectionGapDesktop: '9rem',
    homeMaxSections: 11,
    contentMaxWidthDisplay: '22ch',
    contentMaxWidthLede: '48ch',
    contentMaxWidthProse: '68ch',
    contentMaxWidthEditorial: '72rem',
    contentMaxWidthFullBleed: '100vw',
    gutterMobile: '1.5rem',
    gutterDesktop: '4rem',
  },
  // Hero CSS hooks for the cinematic full-bleed variant.
  // Light-register overlay scrim: gradient fades from transparent at top to
  // 85% cream at bottom. This LIGHTENS the bottom of the photo so dark hero
  // text (vs editorial-dark's light text) stays legible regardless of source
  // image. Bottom alpha and direction mirror dark; only the chromatic color
  // flips from near-black to cream.
  hero: {
    aspectMobile: '4 / 5',
    aspectDesktop: '16 / 9',
    markOrigin: 'top-left',
    textOrigin: 'bottom-left',
    // ⚠️ HAL-1174 RETUNED THIS, AND THE OLD VALUE WAS A CONTRAST BUG, not a
    // preference. It ramped 0.10 -> 0.85 across the WHOLE hero, which means the
    // band where the copy stack actually sits -- the bottom ~30% -- was still
    // reading the photograph at close to full strength. Sampled off the rendered
    // sf-concierge-psychiatry homepage, the hero bullet sat on a sunlit blazer at
    // roughly 2.5:1 against a 4.5:1 requirement.
    //
    // `pnpm validate:a11y` reported ZERO errors on that page and always would:
    // it reads a colour off an element and looks for a background behind it, and
    // here the background is a CSS gradient composited over a photograph, which
    // it does not evaluate. Do not read a green a11y run as evidence about text
    // on a scrimmed image -- sample the rendered pixels.
    //
    // The new ramp holds transparent through the top 30% (the photo's subject is
    // untouched), then climbs hard so the copy band is near-solid cream. HR-11
    // is unchanged: the scrim still LIGHTENS toward cream, inverse to
    // editorial-dark. Only the curve moved.
    overlayScrim:
      'linear-gradient(180deg, rgba(250, 247, 242, 0) 30%, rgba(250, 247, 242, 0.62) 55%, rgba(250, 247, 242, 0.93) 78%, rgba(250, 247, 242, 0.97) 100%)',
    // ⚠️ HAL-1223 measured this curve failing WCAG AA on the hero, and the
    // HAL-1174 note beside it is wrong about why: the hero became a four-frame
    // slideshow in HAL-1205, so "desktop measures clean" was one frame of
    // four, and the binding run is the KICKER rather than the H1 -- topmost,
    // so it sits where a bottom-anchored scrim is weakest, and 14px accent
    // green needs 4.5:1 from the darkest foreground on the page.
    //
    // The curve is still NOT retuned, and this time no token was added either.
    // No gradient can do this job: a stop is positioned against the HERO while
    // the copy sits at a height set by its own type, so a curve tuned at one
    // window or one provider is wrong at the next. Four families of gradient
    // were measured and all four lost. The fix is structural instead -- on
    // phones the copy comes off the photograph entirely and sits on
    // `--color-background`, so no scrim is involved in its contrast at all.
    // The measurements, and the four rejected alternatives, are in
    // `EditorialHero.css` § "Mobile hero: stack the copy under the
    // photograph". Before and after runs are committed under
    // `data/a11y/<provider>/2026-08-25/`, which also carries the method.
    //
    // HR-11 is intact where it is load-bearing: this gradient is unchanged and
    // still lightens toward cream, inverse to editorial-dark, and it is what
    // desktop still uses. Only the mobile composition moved.
    imageFadeDuration: '400ms',
  },
  // Named font pairings (B2 library shape). Same as editorial-dark – Manrope
  // handles both display and body weight tiers.
  //
  // Font files are self-hosted under `/fonts/editorial-light/manrope/...`. The
  // path is forked from `/fonts/editorial-dark/` so each preset's deploy gate
  // can rebuild independently, but the woff2 binaries are identical. Operators
  // copy the dark woff2 directory into the light path during provisioning.
  fontPairings: {
    'manrope-only': {
      id: 'manrope-only',
      label: 'Manrope (display + body)',
      default: true,
      fontFamilyDisplay: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyHeading: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyBody: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyUi: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontWeightDisplay: '800',
      fontWeightHeading: '800',
      fontWeightBody: '400',
      fontWeightUi: '600',
      trackingDisplay: '-0.02em',
      trackingHeading: '-0.01em',
      trackingBody: '0',
      trackingLabel: '0.16em',
      trackingWordmark: '0.18em',
      files: [
        // Preloaded: hero display weight (Manrope 800).
        {
          family: 'Manrope',
          weight: '800',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-800.woff2',
          format: 'woff2',
          preload: true,
          display: 'optional',
        },
        // Body regular (Manrope 400).
        {
          family: 'Manrope',
          weight: '400',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-400.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // UI medium (kicker / label tier).
        {
          family: 'Manrope',
          weight: '500',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-500.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // UI semibold (CTA buttons, two-tone accent spans).
        {
          family: 'Manrope',
          weight: '600',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-600.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // Heading bold (for H2/H3 where 800 is too heavy).
        {
          family: 'Manrope',
          weight: '700',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-700.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
      ],
    },
    // Serif display pairing (HAL-1160). Newsreader 400 for display and
    // headings, Manrope for body and UI. Opt-in per provider via
    // `theme.fontPairing: newsreader-manrope`; `manrope-only` stays the
    // preset default and no existing provider moves.
    //
    // WHY A SECOND PAIRING RATHER THAN A DIFFERENT PRESET. SG-1 ("Manrope
    // across the page, single-family register") is soft guidance, and the
    // register it protects is a real distinction – light ground + serif
    // headings is cosmetic-atelier's signature. This is a knowing, narrow
    // deviation for one provider whose origin site set its headings in a
    // 400-weight serif (Libre Baskerville) across every page, including the
    // nine modality pages Halcy now serves. Moving him to cosmetic-atelier
    // to keep the serif would have imported an entire component library, a
    // scroll-reveal motion layer, and a gallery-first composition contract
    // that a solo psychiatry practice has no use for. HR-6 is untouched:
    // self-hosted woff2, exactly one preloaded face, no Google CDN.
    //
    // ⚠️ WEIGHTS BELOW ARE DOCUMENTATION, NOT WIRING. `applySelectedPairingFamilies()`
    // in `packages/themes/src/index.ts` points the typography slots at a
    // pairing's FAMILIES only and deliberately never touches weights, so
    // `--font-weight-heading` keeps coming from `typography.fontWeightHeading`
    // – which is `800` on this preset. A provider adopting this pairing MUST
    // also set `theme.fonts.headingWeight: 400`, or every heading renders a
    // synthesized bold of a 400-only file. That combination (a `fonts.*`
    // override alongside an explicit `fontPairing`) is the sanctioned HAL-464
    // escape hatch, not a smell – `runFontPreloadsGate` forbids `fonts` ALONE,
    // and `getGoogleFontsUrl()` returns undefined whenever `fontPairing` is
    // set, so no CDN request is reintroduced.
    //
    // cosmetic-atelier never hit this because its own
    // `typography.fontWeightHeading` is already '400', matching both of its
    // pairings; editorial-light's is '800', so the mismatch is real here.
    //
    // NO ITALIC FACE is vendored. Newsreader's drawn italic exists (it ships
    // under `cosmetic-atelier/`) but no editorial-light HEADING takes an
    // `emphasis: 'italic'` span today – the two italic spans on this
    // provider's homepage are in `body`, which renders in Manrope. Add
    // `newsreader-400-italic.woff2` at the moment a heading needs it; a
    // synthesized oblique of a high-contrast serif is visibly wrong, and
    // shipping a face nothing references is dead weight on the LCP path.
    'newsreader-manrope': {
      id: 'newsreader-manrope',
      label: 'Newsreader (display + headings) + Manrope (body + UI)',
      fontFamilyDisplay: 'Newsreader, Georgia, "Times New Roman", serif',
      fontFamilyHeading: 'Newsreader, Georgia, "Times New Roman", serif',
      fontFamilyBody: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyUi: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontWeightDisplay: '400',
      fontWeightHeading: '400',
      fontWeightBody: '400',
      fontWeightUi: '600',
      // Looser than `manrope-only`'s -0.02em, recording what this face wants at
      // display size. NOTE these tracking fields are currently INERT on every
      // pairing – no code path emits letter-spacing from a pairing – so this is
      // the intended value, not a rendered one.
      trackingDisplay: '-0.012em',
      trackingHeading: '-0.008em',
      trackingBody: '0',
      trackingLabel: '0.16em',
      trackingWordmark: '0.18em',
      files: [
        // Preloaded: the display face. Exactly one, per HR-6.
        {
          family: 'Newsreader',
          weight: '400',
          style: 'normal',
          url: '/fonts/editorial-light/newsreader/newsreader-400.woff2',
          format: 'woff2',
          preload: true,
          display: 'optional',
        },
        // Body regular.
        {
          family: 'Manrope',
          weight: '400',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-400.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // UI medium (kicker / label tier).
        {
          family: 'Manrope',
          weight: '500',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-500.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // UI semibold (CTA buttons, nav, two-tone accent spans).
        {
          family: 'Manrope',
          weight: '600',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-600.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // Bold, for the few UI surfaces that ask for it (card labels).
        {
          family: 'Manrope',
          weight: '700',
          style: 'normal',
          url: '/fonts/editorial-light/manrope/manrope-700.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
      ],
    },
  },
});
